import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { shallowEqual, useSelector } from 'react-redux';
import { hasProdLogsEnabled } from './c1c-helper';

/**
 * Redux Store: web-app-workload
 **/

export const initialState = {
  c1Services: { c1ServicesList: [] },
  c1Token: { token: '', expires: '', userUrn: '' },
  c1UserDetails: { name: '', email: '', wsAdminID: '', type: '', hasError: false },
  c1Accounts: { requestAccounts: false, hasError: false, c1AccountID: '', c1AccountsList: [] },
  c1Common: {
    storeId: 'web-app-workload',
    locale: 'en-US',
    theme: { colorMode: 'light', overrides: {} },
  },
};

export function reducer(state = initialState, action) {
  switch (action.type) {
    case 'STORE_INIT':
      // rehydrate the states base on shell state (e.g. upon refresh)
      return {
        ...action.shellInitState,
        c1Common: {
          ...action.shellInitState.c1Common,
          storeId: 'web-app-workload',
        },
      };
    case 'SET_LOCALE':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          locale: action.locale,
        },
      };
    case 'STORE_SERVICES':
      return {
        ...state,
        c1Services: {
          ...state.c1Services,
          c1ServicesList: action.c1ServicesList,
        },
      };
    case 'STORE_C1_TOKEN':
      return {
        ...state,
        c1Token: {
          ...state.c1Token,
          token: action.c1Token.token,
          expires: action.c1Token.expires,
          userUrn: action.c1Token.userUrn,
        },
      };
    case 'ADD_ACCOUNT_ID':
    case 'ADD_LAST_SELECTED_ACCOUNT_ID':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountID: action.c1AccountID,
        },
      };
    case 'SET_THEME':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: action.theme,
        },
      };
    case 'SET_COLOR_MODE':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state.c1Common.theme,
            colorMode: action.colorMode,
          },
        },
      };
    case 'SET_THEME_OVERRIDES':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state.c1Common.theme,
            overrides: action.overrides,
          },
        },
      };
    case 'SET_WS_ADMIN_ID':
      return {
        ...state,
        c1UserDetails: { ...state.c1UserDetails, wsAdminID: action.wsAdminID },
      };
    case 'ADD_USER_TYPE':
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          type: action.userType,
        },
      };
    case 'ADD_USER_INFO':
      const { name = '', email = '', timezone = '' } = action.userDetails;
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          name,
          email,
          timezone,
          hasError: false,
          errorMsg: '',
        },
      };
    case 'HYDRATE_STORE':
      return {
        ...state,
        ...action.storeState,
      };
    case 'RESET_STORE':
      return {
        ...state,
        ...initialState,
      };
    default:
      return state;
  }
}

export const getServices = (state, type) => {
  if (state.c1Services && Object.keys(state.c1Services).length !== 0) {
    return state.c1Services[type];
  }
  return undefined;
};

export const getCommonType = (state, type) => {
  if (state.c1Common && Object.keys(state.c1Common).length !== 0) {
    return state.c1Common[type];
  }
  return undefined;
};

/**
 * Custom Logger Middleware
 * @param store
 * @returns {function(*): function(*=): *}
 */
/**
 * Logs all actions and states after they are dispatched.
 */
const logger = (store) => (next) => (action) => {
  if (
    (DEPLOYMENT_TARGET !== 'prod' && DEPLOYMENT_TARGET !== 'govcloud-prod') ||
    hasProdLogsEnabled
  ) {
    console.group(action.type, 'web-app-workload');
    console.debug('web-app-workload dispatching', action);

    // Call the next dispatch method in the middleware chain
    const result = next(action);

    console.debug('web-app-workload next state after dispatch', store.getState());
    console.groupEnd();
    return result;
  }
  return next(action);
};

const composeEnhancers = composeWithDevTools({ name: 'web-app-workload' });

export const storeInstance = createStore(reducer, composeEnhancers(applyMiddleware(logger)));

export const useShallowEqualSelector = (selector) => useSelector(selector, shallowEqual);
